import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { docPrefixApi, CompanyDocSeries, DocKind } from '../../utils/api/docprefix';
import { loadDocSeries } from '../../utils/docSeries';
import { todayISO } from '../../utils/region';

// ── Admin → Companies → <firm> → Document number prefixes (multi-tenant package "docprefix") ─────────────
// What a firm's quotation / invoice / receipt numbers start with. Server: routes/docSeries.ts →
// services/docSeries.ts (the numberer reads the same values). Empty = the product default, shown as placeholder.
// `letter` is the series letter as typed in the card above, so the example follows it live.
const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 20, marginBottom: 16 };
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const input: React.CSSProperties = {
  width: 120, padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', fontFamily: 'ui-monospace, monospace',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const btn: React.CSSProperties = { padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const KINDS: DocKind[] = ['quotation', 'invoice', 'receipt'];
const LABEL: Record<DocKind, string> = { quotation: 'dpx_quotation', invoice: 'dpx_invoice', receipt: 'dpx_receipt' };

export default function DocSeriesCard({ companyId, letter }: { companyId: string; letter?: string }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState<CompanyDocSeries | null>(null);
  const [vals, setVals] = useState<Record<DocKind, string>>({ quotation: '', invoice: '', receipt: '' });
  const [err, setErr] = useState(''); const [msg, setMsg] = useState(''); const [busy, setBusy] = useState(false);

  const take = (d: CompanyDocSeries) => {
    setInfo(d);
    const next = { quotation: '', invoice: '', receipt: '' } as Record<DocKind, string>;
    for (const s of d.series) if ((KINDS as string[]).includes(s.kind) && s.key === s.kind) next[s.kind] = s.setting;
    setVals(next);
  };
  useEffect(() => {
    let live = true;
    docPrefixApi.company(companyId).then(d => { if (live) take(d); }).catch((e: any) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [companyId]);

  if (!info) return err ? <div style={card}><div style={{ fontSize: 12, color: '#E24B4A' }}>{err}</div></div> : null;
  if (info.companyKind === 'merchant') return <div style={card}><div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('dpx_merchant')}</div></div>;

  const current = (k: DocKind) => info.series.find(s => s.key === k)?.prefix || '';
  const effective = (k: DocKind) => vals[k].trim().toUpperCase() || info.defaults[k] || '';
  const L = String(letter ?? info.letter ?? '').trim().toUpperCase();
  const ym = todayISO().slice(0, 7).replace('-', '');

  const save = async () => {
    setErr(''); setMsg('');
    if (KINDS.some(k => effective(k) !== current(k)) && !window.confirm(t('dpx_confirm_change'))) return;
    setBusy(true);
    try {
      const d = await docPrefixApi.saveCompany(companyId, { quotation: vals.quotation.trim(), invoice: vals.invoice.trim(), receipt: vals.receipt.trim() });
      take(d);
      void loadDocSeries(true);   // every number on screen re-reads the workspace prefixes
      setMsg(t('dpx_saved')); setTimeout(() => setMsg(''), 2000);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('dpx_title')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14 }}>{t('dpx_hint')}</div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12 }}>{err}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {KINDS.map(k => (
          <div key={k}>
            <label style={lbl}>{t(LABEL[k])}</label>
            <input value={vals[k]} maxLength={8} placeholder={info.defaults[k] || ''} style={input}
              onChange={e => setVals(v => ({ ...v, [k]: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))} />
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
              {t('dpx_example')} <span style={{ fontFamily: 'ui-monospace, monospace', color: 'var(--text-secondary)', fontWeight: 600 }}>{`${effective(k)}-${L || '?'}-${ym}00001`}</span>
            </div>
            {!vals[k] && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('dpx_default', { p: info.defaults[k] || '' })}</div>}
          </div>
        ))}
      </div>
      {!L && <div style={{ fontSize: 11, color: '#B45309', marginTop: 10 }}>{t('dpx_no_letter')}</div>}
      {info.retired.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 10 }}>
          {t('dpx_retired')}: {info.retired.map(r => `${r.prefix} (${t(LABEL[r.kind])})`).join(', ')}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
        {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" style={{ marginRight: 4 }} />{msg}</span>}
        <button onClick={save} disabled={busy} style={btn}>{busy ? t('login_wait') : t('save')}</button>
      </div>
    </div>
  );
}
